import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
    ApiClientService,
    AuthenticationService,
    CachedValuesService,
    PermissionService,
    ToastService,
    RightPanelService
} from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';
import { UserForm } from './create-and-update-user-modal.model';
import { CreateUserResponse, UpdateUserResponse } from '@services/api-client/responses';
import { Permission, RepresentationType, Role, ValidationMessageKey } from 'app/enums';
import { matchValidator, passwordValidator } from '@helpers/index';
import { User } from 'app/types';
import { CustomFormModule } from '@shared/custom-form/custom-form.module';
import { SharedModule } from '@shared/shared.module';

@UntilDestroy()
@Component({
    selector: 'app-create-and-update-user-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomFormModule, SharedModule],
    templateUrl: './create-and-update-user-modal.component.html',
    styleUrl: './create-and-update-user-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateAndUpdateUserModalComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly permissionService = inject(PermissionService);
    private readonly rightPanelService = inject(RightPanelService);
    private readonly activeModal = inject(NgbActiveModal, { optional: true });

    @Input() public editUser?: User;
    @Input() public startInEditMode?: boolean;
    public tenantId?: string;
    public isModal: boolean = false;

    public showPotentialValidationMessages = false;
    public loading: boolean = false;
    public userForm: FormGroup<UserForm>;
    public roles = Role;
    public isEditMode = false;
    public showRoles = false;
    private hasAdminRole = this.permissionService.hasAnyPermission([Permission.WriteUser]);

    private readonly dataChangedSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    public dataChangedUser$: Observable<User> = this.dataChangedSubject.asObservable().pipe(
        filter((user) => user != null),
        map((user) => user)
    );

    public get f() {
        return this.userForm.controls;
    }

    public get isMyUser() {
        return this.editUser?.id === this.authenticationService.getUser().userId;
    }

    public get newUser() {
        return !this.editUser;
    }

    public get createUnrestrictedUser() {
        return (
            !this.tenantId &&
            this.authenticationService.getUser().representing.representationType == RepresentationType.UnRestricted &&
            (this.newUser || this.editUser?.roles.length === 0)
        );
    }

    private get hasRolesValidation() {
        return (
            this.showRoles &&
            (this.authenticationService.getUser().representing.representationType !== RepresentationType.UnRestricted ||
                this.createUnrestrictedUser ||
                !!this.tenantId)
        );
    }

    public ngOnInit() {
        const isCreate = !this.editUser;
        this.showRoles = !this.createUnrestrictedUser;

        const fb = new FormBuilder();
        const rolesValidators = this.hasRolesValidation
            ? [
                  (control: AbstractControl) =>
                      Array.isArray(control.value) && control.value.length > 0 ? null : { required: true }
              ]
            : [];
        this.userForm = fb.nonNullable.group({
            firstName: [this.editUser?.firstName || '', [Validators.required]],
            lastName: [this.editUser?.lastName || '', [Validators.required]],
            email: [this.editUser?.email || '', [Validators.required, Validators.email]],
            phone: [this.editUser?.phone || ''],
            password: ['', isCreate ? [Validators.required] : []],
            confirmPassword: ['', isCreate ? [Validators.required] : []],
            createPrivateTenant: [false],
            roles: [this.editUser?.roles || [], rolesValidators]
        });

        // Set cross-field validators only if needed
        const setPasswordValidators = () => {
            const password = this.f.password.value;
            const confirmPassword = this.f.confirmPassword.value;
            const shouldValidate = isCreate || password || confirmPassword;

            if (shouldValidate) {
                this.userForm.setValidators([
                    matchValidator('password', 'confirmPassword', ValidationMessageKey.PasswordsMismatch),
                    passwordValidator('password', ValidationMessageKey.WeakPassword)
                ]);
                this.f.password.setValidators(isCreate ? [Validators.required] : []);
                this.f.confirmPassword.setValidators(isCreate ? [Validators.required] : []);
            } else {
                this.userForm.clearValidators();
                this.f.password.clearValidators();
                this.f.confirmPassword.clearValidators();
            }
            this.f.password.updateValueAndValidity({ emitEvent: false });
            this.f.confirmPassword.updateValueAndValidity({ emitEvent: false });
            this.userForm.updateValueAndValidity({ emitEvent: false });
        };

        // Listen for changes to password fields
        this.f.password.valueChanges.subscribe(() => setPasswordValidators());
        this.f.confirmPassword.valueChanges.subscribe(() => setPasswordValidators());

        setPasswordValidators();

        if (!this.hasAdminRole) {
            this.f.roles.disable();
        }

        // Start in readonly mode if editing existing user, otherwise start in edit mode
        // Can be overridden by startInEditMode input
        this.isEditMode = this.startInEditMode !== undefined ? this.startInEditMode : !this.editUser;
    }

    public isDisabled() {
        return this.loading || !this.userForm.dirty;
    }

    public save() {
        if (this.userForm.dirty) {
            this.showPotentialValidationMessages = true;

            if (this.userForm.valid) {
                this.loading = true;
                this.changeDetectorRef.markForCheck();

                if (this.editUser) {
                    this.apiClientService
                        .updateUser$({
                            tenantId: this.tenantId,
                            userId: this.editUser.id,
                            firstName: this.f.firstName.value,
                            lastName: this.f.lastName.value,
                            email: this.f.email.value,
                            phone: this.f.phone.value,
                            setPassword: this.f.password.value,
                            roles: this.f.roles.value
                        })
                        .pipe(untilDestroyed(this), take(1))
                        .subscribe({
                            next: (response: UpdateUserResponse) => {
                                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                                const message = this.translateService.instant(
                                    'FEATURES.USER.TOAST_MESSAGES.UPDATE_USER_SUCCESS'
                                ) as string;
                                this.cachedValuesService.invalidateCache();
                                this.dataChangedSubject.next(response);
                                this.toastService.open(title, message, { type: 'success' });
                                this.closePanel(response);
                            },
                            error: (err: HttpErrorResponse) => {
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();
                                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                                const apiMessage = (err.error as { message?: string })?.message;
                                const message =
                                    apiMessage ||
                                    (this.translateService.instant(
                                        'FEATURES.USER.TOAST_MESSAGES.UPDATE_USER_ERROR'
                                    ) as string);
                                this.toastService.open(title, message, { type: 'error', progressBar: true });
                            }
                        });
                } else {
                    this.apiClientService
                        .createUser$({
                            tenantId: this.tenantId,
                            firstName: this.f.firstName.value,
                            lastName: this.f.lastName.value,
                            email: this.f.email.value,
                            phone: this.f.phone.value,
                            createPrivateTenant: this.f.createPrivateTenant.value,
                            setPassword: this.f.password.value,
                            roles: this.f.roles.value
                        })
                        .pipe(untilDestroyed(this), take(1))
                        .subscribe({
                            next: (response: CreateUserResponse) => {
                                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                                const message = this.translateService.instant(
                                    'FEATURES.USER.TOAST_MESSAGES.CREATE_USER_SUCCESS'
                                ) as string;
                                this.dataChangedSubject.next(response);
                                this.toastService.open(title, message, { type: 'success' });
                                this.closePanel(response);
                            },
                            error: (err: HttpErrorResponse) => {
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();
                                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                                const apiMessage = (err.error as { message?: string })?.message;
                                const message =
                                    apiMessage ||
                                    (this.translateService.instant(
                                        'FEATURES.USER.TOAST_MESSAGES.CREATE_USER_ERROR'
                                    ) as string);
                                this.toastService.open(title, message, { type: 'error', progressBar: true });
                            }
                        });
                }
            }
        }
    }

    public closePanel(result?: unknown) {
        if (this.isModal && this.activeModal) {
            this.activeModal.close(result);
        } else {
            this.rightPanelService.hide();
        }
    }

    public dismiss() {
        if (this.isModal && this.activeModal) {
            this.activeModal.dismiss();
        } else {
            this.rightPanelService.hide();
        }
    }

    public toggleShowRoles() {
        this.showRoles = !this.showRoles;
        if (this.showRoles) {
            this.f.roles.setValidators([
                (control: AbstractControl) =>
                    Array.isArray(control.value) && control.value.length > 0 ? null : { required: true }
            ]);
        } else {
            this.f.roles.clearValidators();
        }
        this.f.roles.updateValueAndValidity();
        this.changeDetectorRef.markForCheck();
    }

    public enterEditMode() {
        this.isEditMode = true;
        this.changeDetectorRef.markForCheck();
    }

    public cancelEdit() {
        if (this.startInEditMode) {
            this.closePanel();
            return;
        }
        this.isEditMode = false;
        this.showPotentialValidationMessages = false;
        // Reset form to original values
        if (this.editUser) {
            this.userForm.patchValue({
                firstName: this.editUser.firstName || '',
                lastName: this.editUser.lastName || '',
                email: this.editUser.email || '',
                phone: this.editUser.phone || '',
                password: '',
                confirmPassword: '',
                roles: this.editUser.roles || []
            });
            this.userForm.markAsPristine();
        } else {
            this.closePanel();
        }
        this.changeDetectorRef.markForCheck();
    }

    public saveAndExitEdit() {
        this.save();
        // The save method will call closePanel() on success, but if we want to stay in readonly mode:
        // this.isEditMode = false;
    }
}
